import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  FormHelperText,
} from "@mui/material";
import { postgraduateDegreeMap } from "./Fixed Data/MappingsForPG";
import apiClient from "../../../utils/apiClient";

const PostgraduateStep = ({ data, setData, handleNext, handleBack }) => {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchPgData = async () => {
      try {
        const res = await apiClient.get("/counselling/pg-info");

        if (res.data.data.pg_data) {
          const pgData = res.data.data.pg_data;
          // console.log("PG data : ", pgData);
          setData((prev) => ({
            ...prev,
            ...pgData,
            courses: pgData.courses || [],
            marks: pgData.marks || {},
          }));
        }
      } catch (err) {
        console.error("Failed to fetch PG info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPgData();
  }, []);
  const gradingOptions = ["Percentage", "CGPA", "Grades"];
  const degrees = Object.keys(postgraduateDegreeMap);
  const branches = data.degree
    ? Object.keys(postgraduateDegreeMap[data.degree])
    : [];

  const courseOptions =
    data.degree && data.branch
      ? postgraduateDegreeMap[data.degree][data.branch]
      : [];

  const marksOptions = {
    Percentage: ["Not applicable", "30-40%", "40-50%", "50-60%", "Above 60%"],
    CGPA: ["Not applicable", "5-6", "6-7", "7-8", "Above 8"],
    Grades: ["Not applicable", "C", "B", "A", "A+"],
  };

  const [errors, setErrors] = useState({});

  const uiFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "6px",
      "& fieldset": { borderColor: "#c9c9c9" },
      "&:hover fieldset": { borderColor: "#9d9d9d" },
      "&.Mui-focused fieldset": { borderColor: "#000" },
    },
    "& .MuiInputLabel-root": { color: "#444" },
  };

  const updateField = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCourseChange = (course) => {
    let updated = [...(data.courses || [])];

    if (updated.includes(course)) {
      updated = updated.filter((c) => c !== course);
    } else {
      updated.push(course);
    }

    updateField("courses", updated);
    updateField(
      "marks",
      updated.reduce((acc, c) => ({ ...acc, [c]: "" }), {}),
    );
  };

  const handleMarksChange = (course, value) => {
    setData((prev) => ({
      ...prev,
      marks: { ...prev.marks, [course]: value },
    }));
  };

  const isOtherSelected = data.courses?.includes("Others");

  // -------------------------
  // VALIDATION FUNCTION
  // -------------------------
  const validateFields = () => {
    const newErrors = {};

    if (!data.university?.trim())
      newErrors.university = "University name is required";
    if (!data.location) newErrors.location = "Location is required";
    if (!data.years?.trim()) newErrors.years = "Years attended is required";
    if (!data.degree?.trim()) newErrors.degree = "Degree is required";
    if (!data.branch?.trim()) newErrors.branch = "Branch is required";
    if (!data.grading) newErrors.grading = "Select a grading system";

    if (!data.courses || data.courses.length === 0)
      newErrors.courses = "Select at least one course";

    if (isOtherSelected && !data.otherCourse?.trim())
      newErrors.otherCourse = "Please enter the course name";

    if (data.courses && data.grading) {
      data.courses.forEach((course) => {
        if (!data.marks?.[course]) {
          if (!newErrors.marks) newErrors.marks = {};
          newErrors.marks[course] = "Select marks";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = async (data) => {
    await apiClient.post("/counselling/pg-info", { pgData: data });
    onNextClick();
  };
  // -------------------------
  // OVERRIDE handleNext
  // -------------------------
  const onNextClick = () => {
    if (validateFields()) {
      handleNext();
    }
  };

  // if (loading) {
  //   return (
  //     <Box sx={{ p: 5 }}>
  //       <Typography>Loading your postgraduate details...</Typography>
  //     </Box>
  //   );
  // }
  return (
    <Box
      sx={{
        backgroundColor: "#d8f1f6",
        padding: { xs: 3, sm: 5 },
        borderRadius: "40px 40px 0 0",
        minHeight: "100vh",
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontSize: { xs: "22px", sm: "24px", md: "28px" },
          fontWeight: 600,
          mb: 1,
        }}
      >
        Postgraduate Education
      </Typography>

      <Typography sx={{ fontSize: "18px", fontWeight: 600, mb: 4 }}>
        Provide your Postgraduate Academic Details
      </Typography>

      <Grid container spacing={3}>
        {/* University */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            University/College Name*
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter your university name"
            sx={uiFieldStyles}
            value={data.university || ""}
            onChange={(e) => updateField("university", e.target.value)}
            error={!!errors.university}
            helperText={errors.university}
          />
        </Grid>

        {/* Location */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Location / City*
          </Typography>
          <FormControl fullWidth sx={uiFieldStyles} error={!!errors.location}>
            <Select
              displayEmpty
              value={data.location || ""}
              onChange={(e) => updateField("location", e.target.value)}
            >
              <MenuItem value="" disabled>
                Select City
              </MenuItem>
              <MenuItem value="Delhi">Delhi</MenuItem>
              <MenuItem value="Mumbai">Mumbai</MenuItem>
              <MenuItem value="Bengaluru">Bengaluru</MenuItem>
              <MenuItem value="Chennai">Chennai</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          {errors.location && (
            <Typography color="error" fontSize={13}>
              {errors.location}
            </Typography>
          )}
        </Grid>

        {/* Years */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Years Attended*
          </Typography>
          <TextField
            fullWidth
            placeholder="2022 - 2024"
            sx={uiFieldStyles}
            value={data.years || ""}
            onChange={(e) => updateField("years", e.target.value)}
            error={!!errors.years}
            helperText={errors.years}
          />
        </Grid>

        {/* Degree */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Degree*
          </Typography>
          <FormControl fullWidth error={!!errors.degree} sx={uiFieldStyles}>
            <Select
              displayEmpty
              value={data.degree || ""}
              onChange={(e) =>
                setData({
                  ...data,
                  degree: e.target.value,
                  branch: "",
                  courses: [],
                })
              }
            >
              <MenuItem value="" disabled>
                Select Degree
              </MenuItem>
              {degrees.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
            {errors.degree && <FormHelperText>{errors.degree}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Branch */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Branch / Specialization*
          </Typography>
          <FormControl fullWidth error={!!errors.branch} sx={uiFieldStyles}>
            <Select
              displayEmpty
              value={data.branch || ""}
              onChange={(e) =>
                setData({ ...data, branch: e.target.value, courses: [] })
              }
            >
              <MenuItem value="" disabled>
                Select Branch
              </MenuItem>
              {branches.length > 0 &&
                branches.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
            </Select>
            {errors.branch && <FormHelperText>{errors.branch}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Grading */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Select Grading System*
          </Typography>
          <FormControl fullWidth sx={uiFieldStyles} error={!!errors.grading}>
            <Select
              displayEmpty
              value={data.grading || ""}
              onChange={(e) => updateField("grading", e.target.value)}
            >
              <MenuItem value="" disabled>
                Select Grading System
              </MenuItem>
              {gradingOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {errors.grading && (
            <Typography color="error" fontSize={13}>
              {errors.grading}
            </Typography>
          )}
        </Grid>

        {/* Courses */}
        <Grid item xs={12}>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>
            Select Courses*
          </Typography>

          <Box sx={{ background: "white", borderRadius: "10px", padding: 2 }}>
            {courseOptions.length > 0 &&
              courseOptions.map((course) => (
                <FormControlLabel
                  key={course}
                  control={
                    <Checkbox
                      checked={data.courses?.includes(course) || false}
                      onChange={() => handleCourseChange(course)}
                    />
                  }
                  label={course}
                />
              ))}
          </Box>

          {errors.courses && (
            <Typography color="error" fontSize={13} mt={1}>
              {errors.courses}
            </Typography>
          )}
        </Grid>

        {/* Other course */}
        {isOtherSelected && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Enter other course name"
              sx={uiFieldStyles}
              value={data.otherCourse || ""}
              onChange={(e) => updateField("otherCourse", e.target.value)}
              error={!!errors.otherCourse}
              helperText={errors.otherCourse}
            />
          </Grid>
        )}

        {/* Marks */}
        {data.grading &&
          data.courses?.map((course) => (
            <Grid item xs={12} key={course}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Marks for{" "}
                {course === "Others"
                  ? data.otherCourse || "Other Course"
                  : course}
              </Typography>

              <RadioGroup
                value={data.marks?.[course] || ""}
                onChange={(e) => handleMarksChange(course, e.target.value)}
              >
                {marksOptions[data.grading].map((m) => (
                  <FormControlLabel
                    key={m}
                    value={m}
                    control={<Radio />}
                    label={m}
                  />
                ))}
              </RadioGroup>

              {errors.marks?.[course] && (
                <Typography color="error" fontSize={13}>
                  {errors.marks[course]}
                </Typography>
              )}
            </Grid>
          ))}

        {/* Buttons */}
        <Grid item xs={12} mt={5} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderColor: "#2C2C2C",
              color: "#2C2C2C",
              padding: "6px 28px",
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={() => handleNextClick(data)}
            sx={{
              backgroundColor: "#2C2C2C",
              color: "white",
              padding: "6px 28px",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#444" },
            }}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PostgraduateStep;
